const mean = (xs) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);

const stdev = (xs) => {
  if (xs.length < 2) return 0;
  const avg = mean(xs);
  const variance = xs.reduce((sum, x) => sum + (x - avg) * (x - avg), 0) / (xs.length - 1);
  return Math.sqrt(variance);
};

const downsideDevAnn = (rets) => {
  if (!rets.length) return 0;
  const squares = rets.map((r) => {
    const d = Math.min(0, r);
    return d * d;
  });
  return Math.sqrt(mean(squares)) * Math.sqrt(252);
};

export const sortinoFromDaily = (rets) => {
  const ddAnn = downsideDevAnn(rets);
  return ddAnn > 0 ? (mean(rets) * 252) / ddAnn : 0;
};

const compound = (rets) => rets.reduce((a, r) => a * (1 + r), 1) - 1;

const cagrCalendar = (total, startISO, endISO) => {
  const ms = new Date(endISO).getTime() - new Date(startISO).getTime();
  const years = ms / (1000 * 60 * 60 * 24 * 365.25);
  return years > 0 ? Math.pow(1 + total, 1 / years) - 1 : 0;
};

export const normalizeBacktestResult = (server, runCfgOverride = {}) => {
  const runCfg = runCfgOverride || {};
  const metrics = server?.metrics || server?.stats || {};
  const equityCurve = Array.isArray(server?.equity_curve) ? server.equity_curve : [];
  const ddCurve = Array.isArray(server?.drawdown_curve) ? server.drawdown_curve : [];
  const dailyRets = Array.isArray(server?.daily_returns) ? server.daily_returns : [];

  const totalReturn =
    (typeof metrics.total_return === 'number')
      ? metrics.total_return
      : compound(dailyRets.map((d) => Number(d.ret) || 0));

  const startISO = (metrics.start || equityCurve[0]?.date || runCfg.start_date);
  const endISO = (metrics.end || equityCurve.at(-1)?.date || runCfg.end_date);

  const cagr =
    (typeof metrics.cagr === 'number')
      ? metrics.cagr
      : cagrCalendar(totalReturn, startISO, endISO);

  const volAnn =
    (typeof metrics.vol_annual === 'number')
      ? metrics.vol_annual
      : (stdev(dailyRets.map((d) => Number(d.ret) || 0)) * Math.sqrt(252));

  const dailyRetVals = dailyRets.map((d) => Number(d.ret) || 0);
  const sharpe =
    (typeof metrics.sharpe === 'number')
      ? metrics.sharpe
      : (volAnn > 0 ? (mean(dailyRetVals) * 252) / volAnn : 0);
  const sortino =
    (typeof metrics.sortino === 'number')
      ? metrics.sortino
      : sortinoFromDaily(dailyRetVals);

  const maxDD =
    (typeof metrics.max_drawdown === 'number')
      ? metrics.max_drawdown
      : (() => {
          const vals = ddCurve
            .map((d) => (typeof d.drawdown === 'number') ? d.drawdown : -(Number(d.drawdown_mag) || 0))
            .filter((x) => !Number.isNaN(x));
          return vals.length ? Math.min(...vals) : 0;
        })();

  const benchmarkCurve = Array.isArray(server?.benchmark_curve) ? server.benchmark_curve : [];
  const timeseries = equityCurve.map((pt) => {
    const dd = ddCurve.find((d) => d.date === pt.date);
    const ddMag = dd
      ? (typeof dd.drawdown_mag === 'number'
          ? dd.drawdown_mag
          : Math.abs(Number(dd.drawdown) || 0))
      : 0;
    const bm = benchmarkCurve.find((b) => b.date === pt.date);
    return {
      date: pt.date,
      portfolio_value: Number(pt.equity),
      benchmark_value: bm ? Number(bm.equity) : undefined,
      drawdown: ddMag
    };
  });

  const rebalancesTable = server?.rebalances_table || server?.trades || server?.rebalances || [];
  const tradeFills = server?.trade_fills || server?.tradefills || [];
  const factorAnalysis = server?.factor_analysis || server?.factorAnalysis || [];
  const topContributors = server?.top_contributors || [];
  const topDetractors = server?.top_detractors || [];
  const positionJumps = Array.isArray(server?.position_jumps)
    ? server.position_jumps
    : (Array.isArray(server?.positionJumps) ? server.positionJumps : []);
  const dataHandling = server?.data_handling || server?.dataHandling || null;
  const optimization = server?.optimization || null;
  const isOptimization = Boolean(optimization || server?.is_optimization);

  const tradeMetrics = server?.trade_metrics || {};
  const totalTrades = (typeof tradeMetrics.total_trades === 'number')
    ? tradeMetrics.total_trades
    : (typeof tradeMetrics.trades === 'number')
      ? tradeMetrics.trades
      : tradeFills.length || (server?.trades_roundtrip?.length || 0);
  const winRate = (typeof tradeMetrics.win_rate === 'number')
    ? tradeMetrics.win_rate
    : (typeof metrics.win_rate === 'number' ? metrics.win_rate : 0);
  const avgTradeReturn = (typeof tradeMetrics.avg_trade_return === 'number')
    ? tradeMetrics.avg_trade_return
    : (typeof tradeMetrics.avg_return === 'number' ? tradeMetrics.avg_return : 0);
  const profitFactorTrades = (typeof tradeMetrics.profit_factor_trades === 'number')
    ? tradeMetrics.profit_factor_trades
    : (typeof tradeMetrics.profit_factor === 'number' ? tradeMetrics.profit_factor : 0);
  const avgHoldingDays = (typeof tradeMetrics.avg_holding_days === 'number')
    ? tradeMetrics.avg_holding_days
    : (typeof tradeMetrics.avg_holding === 'number' ? tradeMetrics.avg_holding : 0);

  return {
    summary: {
      total_return: totalReturn,
      annualized_return: cagr,
      volatility: volAnn,
      sharpe_ratio: sharpe,
      sortino_ratio: sortino,
      max_drawdown: Math.abs(maxDD),
      win_rate: winRate,
      total_trades: totalTrades,
      avg_trade_return: avgTradeReturn,
      profit_factor: profitFactorTrades,
      calmar_ratio: (typeof metrics.calmar_ratio === 'number') ? metrics.calmar_ratio : null,
      avg_holding_days: avgHoldingDays
    },
    timeseries,
    trades: rebalancesTable,
    rebalances_table: rebalancesTable,
    trade_fills: tradeFills,
    trades_roundtrip: server?.trades_roundtrip || [],
    explain: server?.explain || [],
    factor_analysis: factorAnalysis,
    top_contributors: topContributors,
    top_detractors: topDetractors,
    position_jumps: positionJumps,
    data_handling: dataHandling,
    warnings: server?.warnings || [],
    defaults_applied: server?.defaults_applied || [],
    params: server?.params || {},
    optimization: optimization || undefined,
    is_optimization: isOptimization
  };
};
